import { useEffect, useMemo, useRef, useState } from 'react';
import { getScrollParent } from '../../../core/dom/getScrollParent.ts';
import { observeResize } from '../../../core/dom/observeResize.ts';
import {
  type IConfigurationCardEntry,
  useConfigurationCardEntries,
} from '../ConfigurationCardContext.tsx';
import { sortEntriesByDomOrder } from '../sortEntriesByDomOrder.ts';
import { findCurrentCardIndex } from './findCurrentCardIndex.ts';

/**
 * Logica della barra di subentro (vista `dense`), separata dal rendering di
 * `ConfigurationCardDenseNav` per testabilità.
 *
 * Traccia lo scroll del container e individua la card con il titolo nascosto
 * (vedi `findCurrentCardIndex`): è il titolo che la barra deve mostrare.
 * `current` è `undefined` finché nessun titolo è nascosto — barra assente.
 * Considera SOLO le card principali (le sub-card con `parentId` sono escluse),
 * riordinate per posizione DOM reale.
 */
export function useConfigurationCardDenseNav() {
  // Tutte le card registrate nel group (via context).
  const entries = useConfigurationCardEntries();
  // Solo le card principali (niente sub-card), riordinate per posizione DOM reale.
  const topLevel = useMemo(
    () => sortEntriesByDomOrder(entries.filter(entry => !entry.parentId)),
    [entries],
  );

  // Ref al contenitore sticky della barra: punto di partenza per trovare lo
  // scroll parent.
  const navRef = useRef<HTMLDivElement>(null);
  // Indice della card col titolo nascosto (-1 = nessuna, barra assente).
  const [currentIndex, setCurrentIndex] = useState(-1);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    // Id del frame in coda (per throttling via rAF) e scroll parent locale
    // all'effetto.
    let raf = 0;
    let scrollParent: HTMLElement | null = null;

    // Ricalcola e aggiorna l'indice della card col titolo nascosto.
    const compute = () => {
      // Senza scroll parent (contenuto che non supera ancora l'altezza del
      // drawer) non c'è scroll: nessun titolo può essere nascosto.
      setCurrentIndex(
        scrollParent ? findCurrentCardIndex(topLevel, scrollParent) : -1,
      );
    };
    // Throttle: accorpiamo più eventi ravvicinati (scroll/resize) in un solo
    // ricalcolo per frame.
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };

    // Lo scroll parent va rivalutato nel tempo: al mount può essere `null` (il
    // contenuto non supera ancora l'altezza del drawer) e diventare scrollabile
    // solo quando il contenuto cresce. Riaggancia il listener di scroll al
    // parent corrente quando cambia.
    const syncScrollParent = () => {
      const next = getScrollParent(nav);
      if (next === scrollParent) return;
      scrollParent?.removeEventListener('scroll', schedule);
      scrollParent = next;
      next?.addEventListener('scroll', schedule, { passive: true });
    };

    // Collapse e campi dinamici cambiano altezze/posizioni delle card SENZA
    // generare scroll/resize: osserviamo le card (e il loro contenitore) così da
    // ricalcolare — e rivalutare lo scroll parent — anche in quei casi.
    const resizeObserver = observeResize(
      [
        nav.parentElement,
        ...topLevel.map(entry => document.getElementById(entry.id)),
      ],
      () => {
        syncScrollParent();
        schedule();
      },
    );

    // Il resize della finestra può cambiare la scrollabilità: ricalcoliamo.
    window.addEventListener('resize', schedule);
    // Aggancio iniziale dello scroll parent + primo calcolo al mount.
    syncScrollParent();
    compute();

    // Cleanup: annulla il frame in coda e stacca tutti i listener/observer.
    return () => {
      cancelAnimationFrame(raf);
      scrollParent?.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      resizeObserver?.disconnect();
    };
  }, [topLevel]);

  // Clamp: le entries possono ridursi (unmount) prima del ricalcolo dell'indice.
  const current: IConfigurationCardEntry | undefined =
    currentIndex >= 0
      ? topLevel[Math.min(currentIndex, topLevel.length - 1)]
      : undefined;

  return {
    /** Ref da applicare al contenitore sticky (serve a trovare lo scroll container). */
    navRef,
    /** Card col titolo nascosto; `undefined` = nessuna, barra assente. */
    current,
    /** Ci sono card principali registrate (per decidere se montare la barra). */
    hasSections: topLevel.length > 0,
  };
}
